// src/components/panels/ProjectDetailsPanel.jsx
import React from "react";
import { format } from 'date-fns';
import { Briefcase, MapPin, FileText, Tag, Calendar, User, Clock, Info, Users } from "lucide-react";

// Reusable component to display a detail item with an icon.
const DetailItem = ({ icon, label, value }) => {
    const getNestedValue = (val) => {
        if (typeof val === 'object' && val !== null) {
            return `${val.firstName} ${val.lastName}` || 'N/A';
        }
        return val;
    };

    const finalDisplayValue = getNestedValue(value) || "N/A";
    const finalValueClass = finalDisplayValue === "N/A" ? "text-gray-400 font-normal" : "font-semibold text-gray-800";
    
    return (
        <div className="flex items-start py-2">
            <div className="text-orange-500 mr-4 mt-1">{icon}</div>
            <div>
                <p className="text-sm text-gray-500">{label}</p>
                <p className={finalValueClass}>{finalDisplayValue}</p>
            </div>
        </div>
    );
};

const ProjectDetailsPanel = ({ project }) => {
    if (!project) return null;

    return (
        <div className="flex flex-col h-full overflow-y-auto">
            <section className="grid grid-cols-1 gap-x-8 gap-y-4 mb-4">
                <DetailItem icon={<Briefcase size={20} />} label="Project Title" value={project.projectTitle} />
                <DetailItem icon={<MapPin size={20} />} label="Location" value={project.location} />
                <DetailItem icon={<Tag size={20} />} label="Categories" value={project.projectCategories} />
                <DetailItem icon={<Info size={20} />} label="Status" value={project.status} />
            </section>
            
            <hr className="my-2" />

            <section className="flex-grow flex flex-col">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Project Overview</h3>
                <DetailItem icon={<FileText size={20} />} label="Description" value={project.description} />
                <DetailItem icon={<Calendar size={20} />} label="Start Date" value={format(new Date(project.startDate), 'dd MMM yyyy')} />
                <DetailItem icon={<Calendar size={20} />} label="End Date" value={format(new Date(project.endDate), 'dd MMM yyyy')} />
                <DetailItem icon={<Clock size={20} />} label="Duration" value={`${project.duration || 'N/A'} days`} />
                <DetailItem icon={<User size={20} />} label="Program Manager" value={project.assignedProgramManager} />
            </section>
            
            <hr className="my-2" />

            <section>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Donor Information</h3>
                <div className="bg-white p-3 rounded-lg max-h-40 overflow-y-auto">
                    {project.donor && project.donor.length > 0 ? (
                        <div className="space-y-3">
                            {project.donor.map((donorItem) => (
                                <div key={donorItem._id} className="border-b border-gray-100 last:border-b-0 pb-2">
                                    <p className="font-medium text-gray-900 flex items-center">
                                        <Users size={16} className="mr-2 text-orange-500" />
                                        {donorItem.name}
                                    </p>
                                    <p className="text-sm text-gray-600 pl-6">
                                        Email: {donorItem.email || 'N/A'}
                                    </p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-500 text-center text-sm">No donors assigned.</p>
                    )}
                </div>
            </section>
        </div>
    );
};

export default ProjectDetailsPanel;